/* eslint no-console: ["error", { allow: ["error"] }] */
import _ from 'underscore';
import queryString from 'query-string';

export const isIE = () => {
  const ua = window.navigator.userAgent;
  return ua.search(/(MSIE |Trident\/)/g) >= 0;
};

export const isMobile = () => /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
  navigator.userAgent.toLowerCase()
);

export const iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);

export const android = !!navigator.userAgent && /Android/.test(navigator.userAgent);

const runOutsideProduction = (f) => (process.env.DEPLOY_ENV === 'prod' ? _.noop : f);

const isStageEnv = process.env.DEPLOY_ENV === 'stage';

// Currently the namespace doesn't make its way into our js code so check the url path
export const isSandbox = (
  isStageEnv && window.location.hostname.includes('sandbox.stage.transloc.com')
);

export const logger = {
  error: runOutsideProduction(console.error),
};

export const positionBounds = positions =>
  positions.reduce(
    (bounds, position) => ({
      sw: {
        lat: Math.min(bounds.sw.lat, position.lat),
        lng: Math.min(bounds.sw.lng, position.lng),
      },
      ne: {
        lat: Math.max(bounds.ne.lat, position.lat),
        lng: Math.max(bounds.ne.lng, position.lng),
      },
    }),
    {sw: {lat: 90, lng: 180}, ne: {lat: -90, lng: -180}}
  );

export const leftPad = (str, length, pad = '0') => {
  let s = `${str}`;
  while (s.length < length) {
    s = `${pad}${s}`;
  }
  return s;
};

export const secondsToString = (seconds, twelveHour = false) => {
  if (!_.isNumber(seconds) || seconds < 0) {
    return '';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (twelveHour) {
    if (hours === 0) return `12:${leftPad(minutes, 2)}am`;
    if (hours === 12) return `12:${leftPad(minutes, 2)}pm`;
    if (hours > 23) return '11:59pm';
    if (hours > 12) return `${leftPad(hours - 12, 2)}:${leftPad(minutes, 2)}pm`;
    return `${leftPad(hours, 2)}:${leftPad(minutes, 2)}am`;
  }
  return `${leftPad(hours, 2)}:${leftPad(minutes, 2)}`;
};

export const stringToSeconds = (str) => {
  const match = str.match(/^([0-9]{1,2}):([0-5]\d)([ap]?m?)$/);
  if (!match) return null;
  const [, hourString, minuteString, suffixString] = match;
  const hours = parseInt(hourString, 10) * 3600;
  const minutes = parseInt(minuteString, 10) * 60;
  let hourAdjustment = 0;
  if (hourString === '12' && suffixString[0] === 'a') hourAdjustment = -12 * 3600;
  if (hourString !== '12' && suffixString[0] === 'p') hourAdjustment = 12 * 3600;
  return hours + hourAdjustment + minutes;
};

export const naturalSort = {
  _tryInt(s) {
    const n = parseInt(s, 10);
    if (isNaN(n)) return s;
    return n;
  },

  _compareArrays(a, b) {
    // Compare two arrays, one index at a time. Items are compared
    // according to the natural sort rules.
    // Returns -1 if the first array should come before the second, 0 if
    // they are the same rank, and 1 if the first array should come after
    // the second.
    const len = Math.min(a.length, b.length);

    for (let i = 0; i < len; i++) {
      const c = this._tryInt(a[i]);
      const d = this._tryInt(b[i]);
      if (c === d) continue;
      if (typeof (c) === typeof (d)) return c < d ? -1 : 1;
      return typeof (c) === 'number' ? -1 : 1;
    }

    if (a.length < b.length) return -1;
    if (a.length > b.length) return 1;
    return 0;
  },

  _key(_s) {
    let s = _s;
    if (s === null || s === undefined) {
      s = '';
    }
    return s.match(/(\d+|\D+)/g) || [];
  },

  compare(a, b) {
    // Compare two strings according to natural sort rules.
    // Returns -1 if the first string should come before the second, 0 if
    // they are the same rank, and 1 if the first string should come after
    // the second.
    return this._compareArrays(this._key(a), this._key(b));
  },
};

// Split string into its component words.
const _splitWords = (str) => str.trim().split(' ').filter((word) => word);

/**
 * Splits a string into words using `_splitWords`, applies
 * an optional transformation function to each word, capitalizes
 * the first letter of each word, and glues the result back
 * together with `separator`.
 *
 * @param {string} separator
 * @param {Function} wordFn? - an optional function to be mapped
 *  onto each word; used when additional processing is needed
 *  besides capitalizing the first letter of each word
 * @returns {Function}
 */
const capitalizeWords = (separator, wordFn = _.identity) =>
  /**
   * @param {string} str
   * @returns {string}
   */
  str => _.chain(_splitWords(str))
    .map(wordFn)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .value()
    .join(separator);

// Convert input string to PascalCase.
export const toPascalCase = capitalizeWords(
  '',
  word => word.toLowerCase(),
);

export const toTitleCase = capitalizeWords(' ');

// Convert input string to camelCase.
export const toCamelCase = (str) => {
  const result = toPascalCase(str);
  return result[0].toLowerCase() + result.slice(1);
};

// Convert input string to snake_case.
export const toSnakeCase = (str) => _splitWords(str.toLowerCase()).join('_');

export const omitNull = _.partial(_.omit, _, (value) => value === null);

/* Update location parameters with the given values. */
export const updateParams = (currLocation, params) => {
  const query = queryString.parse(currLocation.search);
  const newQuery = queryString.stringify(omitNull(Object.assign({}, query, params)));
  return `${currLocation.pathname}?${newQuery}`;
};

export const removeParams = (currLocation, ...params) => {
  const query = queryString.parse(currLocation.search);
  _.each(params, (param) => {
    query[param] = null;
  });
  const newQuery = queryString.stringify(omitNull(Object.assign({}, query)));
  return `${currLocation.pathname}?${newQuery}`;
};

export const getParam = (currLocation, param) => {
  const query = queryString.parse(currLocation.search);
  return query[param];
};

export const pluralizePhrase = (count, singular, plural) => {
  const phrase = count === 1 ? singular : plural;
  return _.isString(phrase) ? phrase : phrase(count);
};

/**
 * Generates a pair of a formatted number and appropriate plural of a noun
 * for a given count and noun.
 *
 * @param {number} count
 * @param {string} singular
 * @param {string} [plural] - if `singular` is null, defaults to suffixing "-s"
 * @returns {string}
 */
export const pluralize = (count, singular, plural = `${singular}s`) => {
  return pluralizePhrase(
    count,
    `1 ${singular}`,
    (count) => `${(count || 0).toLocaleString()} ${plural}`
  );
};

export const timeFormats = {
  TWELVE_HOUR: '12hr',
  TWENTY_FOUR_HOUR: '24hr',
};

export const copyToClipboard = (value) => {
  const textField = document.createElement('textarea');
  textField.innerText = value;
  textField.style.height = 0;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand('copy');
  textField.remove();
};



// WEBPACK FOOTER //
// ./src/common/utils/index.js